import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Package, Truck, MapPin, LogOut, User, AlertCircle, CheckCircle, RefreshCw, Calendar, Clock, Phone, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import { agentAuthService } from '../../services/agentAuthService';
import { agentDashboardService } from '../../services/agentDashboardService';
import { getValidStatusCodes, getStatusLabel } from '../../services/containerService';
import DeliveryModeModal from './components/DeliveryModeModal';
import ContainerShipmentsModal from './components/ContainerShipmentsModal';
import Icon from '../../components/AppIcon';
import { trackContainerEvent, trackDeliveryTask, trackButtonClick, trackAuth, trackSearch } from '../../services/analyticsService';


/**
 * Dashboard Agent Abidjan
 * Mobile-first dashboard for Abidjan agents
 * Shows containers, deliveries, and delivery tasks
 */
export default function DashboardAgentAbidjan() {
  const navigate = useNavigate();
  const [agentData, setAgentData] = useState(null);
  const [containersWithStats, setContainersWithStats] = useState([]);
  const [containers, setContainers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [pickupTasks, setPickupTasks] = useState([]);
  const [deliveryTasks, setDeliveryTasks] = useState([]);
  const [taskCounters, setTaskCounters] = useState({
    A_PREPARER: 0,
    EN_LIVRAISON: 0,
    LIVRE: 0,
    PROBLEME: 0,
    total: 0
  });
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedContainer, setSelectedContainer] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setError('');
      
      // Get agent session
      const session = await agentAuthService?.getSession();
      if (!session) {
        navigate('/agent-abidjan-login');
        return;
      }

      setAgentData(session?.employee);

      // Load dashboard data in parallel
      const [containersStatsData, containersData, deliveriesData, tasksData, deliveryTasksData, countersData] = await Promise.all([
        agentDashboardService?.getAbidjanContainersWithDeliveryStats(),
        agentDashboardService?.getAbidjanContainers(),
        agentDashboardService?.getDeliveriesToPrepare(),
        agentDashboardService?.getAgentPickupTasks(session?.employee?.id),
        agentDashboardService?.getAgentDeliveryTasks(session?.employee?.id, statusFilter, dateFilter),
        agentDashboardService?.getDeliveryTaskCounters(session?.employee?.id)
      ]);

      setContainersWithStats(containersStatsData);
      setContainers(containersData);
      setDeliveries(deliveriesData);
      setPickupTasks(tasksData);
      setDeliveryTasks(deliveryTasksData);
      setTaskCounters(countersData);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    trackButtonClick('refresh_agent_dashboard');
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleSignOut = async () => {
    try {
      trackAuth('logout', 'agent_abidjan');
      await agentAuthService?.signOut();
      navigate('/agent-abidjan-login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'arrived_abidjan': { color: 'bg-blue-100 text-blue-800', label: 'Arrivé Abidjan' },
      'customs_clearance': { color: 'bg-yellow-100 text-yellow-800', label: 'Dédouanement' },
      'warehouse_abidjan': { color: 'bg-green-100 text-green-800', label: 'Entrepôt Abidjan' },
      'customs': { color: 'bg-yellow-100 text-yellow-800', label: 'Douane' },
      'out_for_delivery': { color: 'bg-purple-100 text-purple-800', label: 'En livraison' },
      'A_PREPARER': { color: 'bg-gray-100 text-gray-800', label: 'À préparer' },
      'EN_LIVRAISON': { color: 'bg-blue-100 text-blue-800', label: 'En livraison' },
      'LIVRE': { color: 'bg-green-100 text-green-800', label: 'Livré' },
      'INCIDENT': { color: 'bg-red-100 text-red-800', label: 'INCIDENT', icon: AlertTriangle },
      'PROBLEME': { color: 'bg-red-100 text-red-800', label: 'Problème' }
    };

    const config = statusConfig?.[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    const StatusIcon = config?.icon;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        {StatusIcon && <StatusIcon className="w-3 h-3" />}
        <span>{config?.label}</span>
      </span>
    );
  };

  const getContainerStatusBadge = (status) => {
    const statusConfig = {
      'EN_ATTENTE': { color: 'bg-yellow-100 text-yellow-800', label: 'En attente', icon: Clock },
      'en_distribution': { color: 'bg-orange-100 text-orange-800 border-2 border-orange-400', label: '⚠️ EN DISTRIBUTION (Alerte)', icon: AlertTriangle },
      'termine': { color: 'bg-green-100 text-green-800', label: '✓ Terminé', icon: CheckCircle },
      'EN_DISTRIBUTION': { color: 'bg-blue-100 text-blue-800', label: 'En distribution', icon: Truck },
      'TERMINE': { color: 'bg-green-100 text-green-800', label: 'Terminé', icon: CheckCircle }
    };

    const config = statusConfig?.[status] || { color: 'bg-gray-100 text-gray-800', label: status, icon: Package };
    const Icon = config?.icon;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon className="w-3 h-3" />
        <span>{config?.label}</span>
      </span>
    );
  };

  const handleFilterChange = (newFilter) => {
    setStatusFilter(newFilter);
    loadDashboardData();
  };

  const handleStatusFilterChange = (newFilter) => {
    setStatusFilter(newFilter);
    trackSearch('delivery_task_filter_status', newFilter);
  };

  const handleDateFilterChange = (newDate) => {
    setDateFilter(newDate);
    if (newDate) {
      trackSearch('delivery_task_filter_date', newDate);
    }
  };

  const handleTaskClick = (task) => {
    trackDeliveryTask('viewed', {
      id: task?.id,
      status: task?.status,
      delivery_zone: task?.delivery_zone
    });
    setSelectedTask(task);
  };

  const handleCloseModal = () => {
    setSelectedTask(null);
  };

  const handleTaskUpdate = () => {
    loadDashboardData();
  };

  const handleContainerClick = (container) => {
    trackContainerEvent('viewed', {
      id: container?.id,
      reference: container?.container_code,
      status: container?.status
    });
    setSelectedContainer(container);
  };

  const handleCloseContainerModal = () => {
    setSelectedContainer(null);
  };

  const handleContainerOpenSuccess = () => {
    // Refresh dashboard data after container is opened
    loadDashboardData();
  };

  const calculateTimeEstimate = (remainingTasks) => {
    const minutesPerDelivery = 25;
    const totalMinutes = remainingTasks * minutesPerDelivery;
    const hours = Math?.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };

  const handleMoveTaskUp = async (taskId, currentOrder, currentZone) => {
    if (!currentOrder || currentOrder === 1) return;
    
    try {
      await agentDashboardService?.updateRouteOrder(taskId, currentOrder - 1);
      await loadDashboardData();
    } catch (err) {
      console.error('Error moving task up:', err);
      setError('Erreur lors du réordonnancement');
    }
  };

  const handleMoveTaskDown = async (taskId, currentOrder, currentZone) => {
    if (!currentOrder) return;
    
    try {
      await agentDashboardService?.updateRouteOrder(taskId, currentOrder + 1);
      await loadDashboardData();
    } catch (err) {
      console.error('Error moving task down:', err);
      setError('Erreur lors du réordonnancement');
    }
  };

  // Update the getStatusColor function
  const getStatusColor = (status) => {
    const colors = {
      'AU_PORT_LE_HAVRE_ANVERS': 'bg-gray-100 text-gray-800',
      'EN_TRANSIT_MARITIME': 'bg-blue-100 text-blue-800',
      'ARRIVE_A_ABIDJAN': 'bg-purple-100 text-purple-800',
      'DEDOUANEMENT_EN_COURS': 'bg-yellow-100 text-yellow-800',
      'TRANSFERT_A_ENTREPOT': 'bg-orange-100 text-orange-800',
      'DISTRIBUTION_EN_COURS': 'bg-indigo-100 text-indigo-800',
      'CONTENEUR_TERMINE': 'bg-green-100 text-green-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-screen px-4">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-red-500 mb-4" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Erreur</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/agent-abidjan-login')}
              className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors min-h-[44px] text-sm sm:text-base"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard Agent Abidjan - ADT Tracking</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Mobile Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
          <div className="px-3 sm:px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <User className="text-green-600" size={18} />
                </div>
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-gray-900">{agentData?.name}</h1>
                  <p className="text-xs sm:text-sm text-gray-600">{agentData?.role}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="p-2.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 active:bg-green-200 disabled:bg-gray-100 disabled:text-gray-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Rafraîchir"
                >
                  <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                </button>
                <button
                  onClick={handleSignOut}
                  className="p-2.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Déconnexion"
                >
                  <LogOut size={18} />
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="px-3 sm:px-4 py-4 sm:py-6">
          {/* Agent Info Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 rounded-full p-3">
                <User className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-gray-900">{agentData?.name}</h2>
                <p className="text-sm text-gray-600">{agentData?.zone}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Rôle</p>
                <p className="text-sm font-medium text-green-600">Agent Abidjan</p>
              </div>
            </div>
          </div>

          {/* NEW: Containers by Container View */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Package className="w-5 h-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Conteneurs Destination Abidjan</h2>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {containersWithStats?.length === 0 ? (
                <div className="p-8 text-center">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Aucun conteneur pour Abidjan</p>
                </div>
              ) : (
                containersWithStats?.map((container) => (
                  <div 
                    key={container?.id} 
                    onClick={() => handleContainerClick(container)}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{container?.containerCode}</h3>
                          {getContainerStatusBadge(container?.status)}
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>Arrivée: {new Date(container?.etaDate)?.toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Counter */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Truck className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">Livraisons</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-green-600">{container?.deliveredCount}</span>
                          <span className="text-sm text-gray-500">/</span>
                          <span className="text-lg font-bold text-gray-900">{container?.totalDeliveries}</span>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      {container?.totalDeliveries > 0 && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full transition-all duration-300"
                              style={{
                                width: `${(container?.deliveredCount / container?.totalDeliveries) * 100}%`
                              }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1 text-center">
                            {Math.round((container?.deliveredCount / container?.totalDeliveries) * 100)}% complété
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Click indicator */}
                    <div className="mt-3 pt-3 border-t border-gray-100 text-center">
                      <span className="text-xs text-green-600 font-medium">
                        Cliquer pour ouvrir et voir les expéditions →
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Conteneurs</p>
                  <p className="text-2xl font-bold text-gray-900">{containers?.length}</p>
                </div>
                <div className="bg-blue-100 rounded-lg p-3">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Livraisons</p>
                  <p className="text-2xl font-bold text-gray-900">{deliveries?.length}</p>
                </div>
                <div className="bg-purple-100 rounded-lg p-3">
                  <Truck className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Tâches Pickup</p>
                  <p className="text-2xl font-bold text-gray-900">{pickupTasks?.length}</p>
                </div>
                <div className="bg-green-100 rounded-lg p-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Tâches Livraison</p>
                  <p className="text-2xl font-bold text-gray-900">{taskCounters?.total}</p>
                </div>
                <div className="bg-orange-100 rounded-lg p-3">
                  <MapPin className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Tasks Section with Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Truck className="w-5 h-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Tâches de livraison Abidjan</h2>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {/* Status Counters */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                <div className="bg-gray-50 rounded p-2 text-center">
                  <p className="text-xs text-gray-600">À préparer</p>
                  <p className="text-lg font-bold text-gray-900">{taskCounters?.A_PREPARER}</p>
                </div>
                <div className="bg-blue-50 rounded p-2 text-center">
                  <p className="text-xs text-blue-600">En livraison</p>
                  <p className="text-lg font-bold text-blue-900">{taskCounters?.EN_LIVRAISON}</p>
                </div>
                <div className="bg-green-50 rounded p-2 text-center">
                  <p className="text-xs text-green-600">Livré</p>
                  <p className="text-lg font-bold text-green-900">{taskCounters?.LIVRE}</p>
                </div>
                <div className="bg-red-50 rounded p-2 text-center">
                  <p className="text-xs text-red-600">Incident</p>
                  <p className="text-lg font-bold text-red-900">{taskCounters?.INCIDENT || 0}</p>
                </div>
                <div className="bg-orange-50 rounded p-2 text-center">
                  <p className="text-xs text-orange-600">Problème</p>
                  <p className="text-lg font-bold text-orange-900">{taskCounters?.PROBLEME}</p>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e?.target?.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="ALL">Tous les statuts</option>
                      {getValidStatusCodes()?.map(({ code, label }) => (
                        <option key={code} value={code}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {deliveryTasks?.length === 0 ? (
                <div className="p-8 text-center">
                  <Truck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Aucune tâche de livraison assignée</p>
                  <p className="text-sm text-gray-500 mt-1">Les nouvelles tâches apparaîtront ici</p>
                </div>
              ) : (
                deliveryTasks?.map((task, index) => {
                  // Calculate zone tasks for progress
                  const zoneTasks = deliveryTasks?.filter(t => t?.deliveryZone === task?.deliveryZone) || [];
                  const zoneDelivered = zoneTasks?.filter(t => t?.status === 'LIVRE')?.length;
                  const zoneTotal = zoneTasks?.length;
                  const zoneRemaining = zoneTotal - zoneDelivered;
                  
                  return (
                    <div 
                      key={task?.id} 
                      onClick={() => handleTaskClick(task)}
                      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{task?.recipientName}</h3>
                            {task?.deliveryZone && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {task?.deliveryZone}
                              </span>
                            )}
                            {task?.routeOrder && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                #{task?.routeOrder}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{task?.recipientPhone}</p>
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          {getStatusBadge(task?.status)}
                          {task?.deliveryPhotoUrls?.length > 0 && (
                            <span className="text-xs text-gray-500">
                              {task?.deliveryPhotoUrls?.length} photo(s)
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2 mt-3">
                        <div className="flex items-start space-x-2 text-sm">
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <p className="text-gray-900">{task?.deliveryAddress}</p>
                        </div>

                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <p className="text-gray-600">
                              {new Date(task?.plannedDate)?.toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <p className="text-gray-600">{task?.plannedTimeWindow}</p>
                          </div>
                        </div>

                        {/* Zone Progress Bar - Only show if zone has multiple deliveries */}
                        {task?.deliveryZone && zoneTotal > 1 && (
                          <div className="bg-blue-50 rounded-lg p-2 mt-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-blue-700">
                                Zone {task?.deliveryZone}: {zoneDelivered}/{zoneTotal} livrés
                              </span>
                              <span className="text-xs text-blue-600">
                                {calculateTimeEstimate(zoneRemaining)}
                              </span>
                            </div>
                            <div className="w-full bg-blue-200 rounded-full h-1.5">
                              <div
                                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                style={{
                                  width: `${(zoneDelivered / zoneTotal) * 100}%`
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Route Order Controls - Only for A_PREPARER status */}
                        {task?.status === 'A_PREPARER' && task?.routeOrder && (
                          <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-100">
                            <span className="text-xs text-gray-600">Ordre:</span>
                            <button
                              onClick={(e) => {
                                e?.stopPropagation();
                                handleMoveTaskUp(task?.id, task?.routeOrder, task?.deliveryZone);
                              }}
                              disabled={task?.routeOrder === 1}
                              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Monter dans l'ordre"
                            >
                              <ArrowUp className="w-4 h-4 text-green-600" />
                            </button>
                            <button
                              onClick={(e) => {
                                e?.stopPropagation();
                                handleMoveTaskDown(task?.id, task?.routeOrder, task?.deliveryZone);
                              }}
                              className="p-1 rounded hover:bg-gray-200"
                              title="Descendre dans l'ordre"
                            >
                              <ArrowDown className="w-4 h-4 text-green-600" />
                            </button>
                          </div>
                        )}

                        {/* INCIDENT DISPLAY SECTION - Highly visible */}
                        {task?.status === 'INCIDENT' && (
                          <div className="mt-3 bg-red-50 border-2 border-red-300 rounded-lg p-3">
                            <div className="flex items-start space-x-2">
                              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className="text-xs font-bold text-red-800 uppercase">Type d'incident:</span>
                                  <span className="text-sm font-semibold text-red-900">{task?.incidentType}</span>
                                </div>
                                <div>
                                  <span className="text-xs font-bold text-red-800 uppercase block mb-1">Commentaire:</span>
                                  <p className="text-sm text-red-900">{task?.incidentComment}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {task?.deliveryNotes && (
                          <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2">
                            <p className="text-xs text-yellow-800">
                              <strong>Note:</strong> {task?.deliveryNotes}
                            </p>
                          </div>
                        )}

                        {task?.deliveredAt && (
                          <div className="text-xs text-gray-500 mt-2">
                            Livré le {new Date(task?.deliveredAt)?.toLocaleString('fr-FR')}
                          </div>
                        )}
                      </div>

                      {/* Click indicator */}
                      <div className="mt-3 pt-3 border-t border-gray-100 text-center">
                        <span className="text-xs text-green-600 font-medium">
                          Cliquer pour ouvrir le mode livraison →
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Containers Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 sm:mb-6">
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center space-x-2">
                <Package size={20} />
                <span>Conteneurs à Abidjan</span>
              </h2>
            </div>
            <div className="p-3 sm:p-4">
              {containersWithStats?.length === 0 ? (
                <div className="text-center py-6 sm:py-8">
                  <Package className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-3" />
                  <p className="text-sm sm:text-base text-gray-600">Aucun conteneur disponible</p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {containersWithStats?.map((container) => (
                    <div
                      key={container?.id}
                      className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-green-500 active:border-green-600 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                        <div>
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 break-all">
                            {container?.containerCode}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">
                            {getStatusLabel(container?.status)}
                          </p>
                        </div>
                        {getStatusBadge(container?.status)}
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                        <div className="bg-blue-50 rounded-lg p-2 sm:p-3">
                          <p className="text-lg sm:text-xl font-bold text-blue-900">
                            {container?.totalShipments || 0}
                          </p>
                          <p className="text-xs text-blue-700">Envois</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-2 sm:p-3">
                          <p className="text-lg sm:text-xl font-bold text-purple-900">
                            {container?.uniqueQuartiers || 0}
                          </p>
                          <p className="text-xs text-purple-700">Quartiers</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2 sm:p-3 col-span-2 sm:col-span-1">
                          <p className="text-lg sm:text-xl font-bold text-green-900">
                            {container?.readyForDelivery || 0}
                          </p>
                          <p className="text-xs text-green-700">Prêts livraison</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Deliveries Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Truck className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Livraisons à préparer</h2>
              </div>
            </div>
            
            {deliveries?.length === 0 ? (
              <div className="p-8 text-center">
                <Truck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Aucune livraison assignée pour le moment</p>
                <p className="text-sm text-gray-500 mt-1">Les nouvelles livraisons apparaîtront ici</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expéditeur
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Destinataire
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quartier
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Colis à livrer
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deliveries?.map((delivery) => (
                      <tr key={delivery?.id} className="hover:bg-gray-50 transition-colors">
                        {/* Column 1: Expéditeur + Ville */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <p className="font-medium text-gray-900">{delivery?.senderName}</p>
                            <p className="text-sm text-gray-600">{delivery?.senderCity}</p>
                          </div>
                        </td>
                        
                        {/* Column 2: Destinataire + Numéro */}
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <p className="font-semibold text-gray-900">{delivery?.receiverName}</p>
                            <div className="flex items-center space-x-1 mt-1">
                              <Phone className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <p className="text-sm text-gray-700 font-medium">{delivery?.receiverPhone}</p>
                            </div>
                          </div>
                        </td>
                        
                        {/* Column 3: Quartier du destinataire */}
                        <td className="px-4 py-4">
                          <div className="flex items-start space-x-1">
                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-700">{delivery?.receiverAddress}</p>
                          </div>
                        </td>
                        
                        {/* Column 4: Colis à livrer */}
                        <td className="px-4 py-4">
                          {delivery?.description ? (
                            <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1">
                              <p className="text-xs text-blue-800">{delivery?.description}</p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Aucune description</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Delivery Mode Modal */}
      {selectedTask && (
        <DeliveryModeModal
          task={selectedTask}
          onClose={handleCloseModal}
          onUpdate={handleTaskUpdate}
        />
      )}
      {/* Container Shipments Modal */}
      {selectedContainer && (
        <ContainerShipmentsModal
          container={selectedContainer}
          agentEmployeeId={agentData?.id}
          onClose={handleCloseContainerModal}
          onSuccess={handleContainerOpenSuccess}
        />
      )}
    </>
  );
}